import { FieldValidator } from 'final-form'
import _ from 'lodash'

import { Dictionary } from './types'

export interface LocalizedMessage {
  translationKey: string
  interpolation?: Record<string, React.ReactText>
  defaultMessage?: string
}

export const createFormValidator =
  <T extends Dictionary<any>>(validators: Partial<Record<keyof T, FieldValidator<any>[]>>) =>
  (props?: any) =>
  (values: T) => {
    const errors = _.reduce(
      validators,
      (acc, _, key) => {
        if (!validators[key]) return acc
        // @ts-ignore
        const error = composeValidators(...validators[key])(values[key], values, { data: props })
        if (error) acc[key] = error
        return acc
      },
      {} as Dictionary<LocalizedMessage | undefined>
    )
    return _.isEmpty(errors) ? undefined : errors
  }

export const composeValidators =
  (...validators: FieldValidator<any>[]) =>
  (value: any, allValues: Dictionary<any> | undefined, meta?: any) =>
    _.reduce(validators, (error, validator) => error || validator(value, allValues || {}, meta), undefined)

export type FieldInterceptor<T> = (v: T) => T
export const composeInterceptors = <T>(...interceptors: FieldInterceptor<T>[]): FieldInterceptor<T> => {
  return _.flow(interceptors)
}

export const maxLengthInterceptor = (maxLength: number) => (val: string) =>
  _.truncate(val, { length: maxLength, omission: '' })

export function error(translationKey: string, interpolation?: any, defaultMessage?: string): LocalizedMessage {
  return {
    translationKey,
    interpolation,
    defaultMessage
  }
}

/* eslint-disable no-control-regex */
const emailRegex =
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/

export const email = (): FieldValidator<string> => v =>
  _.isEmpty(v) || emailRegex.test(v) ? undefined : error('Email')

const isNilOrEmptyString = (value: any) => _.isNil(value) || value === ''

export const required = (): FieldValidator<any> => value => (isNilOrEmptyString(value) ? error('Required') : undefined)

export const requiredIf =
  (condition: (allValues: any, meta: any) => boolean): FieldValidator<any> =>
  (value, allValues, meta) =>
    isNilOrEmptyString(value) && condition(allValues, meta) ? error('Required') : undefined

export const requiredTrue = (): FieldValidator<boolean | null | undefined> => value =>
  !value ? error('Required') : undefined

export const requiredTrueIf =
  (condition: (allValues: any, meta: any) => boolean): FieldValidator<boolean | null | undefined> =>
  (value, allValues, meta) =>
    !value && condition(allValues, meta) ? error('Required') : undefined

export const isTrue = (msg: LocalizedMessage) => (v: boolean) => (v === true ? undefined : msg)

export const minLength =
  <T = any>(min: number): FieldValidator<T> =>
  value =>
    _.size(String(value)) < min ? error('MinLength', { min }) : undefined

export const maxLength =
  <T = any>(max: number): FieldValidator<T> =>
  value =>
    _.size(String(value)) > max ? error('MaxLength', { max }) : undefined

export const length =
  <T = any>({ min, max }: { min?: number; max?: number }): FieldValidator<T> =>
  (value, values, meta) => {
    let res = undefined
    if (!_.isUndefined(min)) res = minLength(min)(value, values, meta)
    if (res) return res
    if (!_.isUndefined(max)) res = maxLength(max)(value, values, meta)
    return res
  }

export const emptyOrLength =
  <T>(params: { min?: number; max?: number }): FieldValidator<T> =>
  (value, values, meta) => {
    if (_.isEmpty(value)) return undefined
    return length<T>(params)(value, values, meta)
  }

export const mustMatch =
  <T = any>(target: string, sourceProperty: string, targetProperty: string): FieldValidator<T> =>
  (value, allValues) =>
    _.isEqual(value, _.get(allValues, target)) ? undefined : error('MustMatch', { sourceProperty, targetProperty })

const containsNumberRegex = /[0-9]/
const containsAlphaRegex = /[a-zA-Z]/
const constainsCapitalRegex = /[A-Z]/
const containsThreeConsecutiveCharsRegex = /(.)\1\1/

export const strongPassword = (): FieldValidator<string> => value => {
  return _.size(value) >= 8 &&
    _.size(value) <= 25 &&
    containsNumberRegex.test(value) &&
    containsAlphaRegex.test(value) &&
    constainsCapitalRegex.test(value) &&
    !containsThreeConsecutiveCharsRegex.test(value)
    ? undefined
    : error('StrongPassword')
}

export const notOldPassword =
  <T = any>(oldPassword: string, sourceProperty: string, targetProperty: string): FieldValidator<T> =>
  (value, allValues) => {
    return _.isEqual(value, _.get(allValues, oldPassword))
      ? error('NotOldPassword', { sourceProperty, targetProperty })
      : undefined
  }

const numericRegex = /^\d+$/

export const phoneNumber = (): FieldValidator<string> => (value, values, meta) => {
  if (_.isEmpty(value)) return undefined
  return numericRegex.test(value) ? maxLength(16)(value, values, meta) : error('Phone')
}

export const onlyNumeric = (): FieldValidator<string> => (value, values, meta) => {
  return numericRegex.test(value) ? maxLength(16)(value, values, meta) : error('OnlyNumeric')
}
